import React, { createContext, useContext, useEffect, useState } from 'react';
import apiClient from '../api/client';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

interface Permission {
  permissionCode: string;
  effect: 'ALLOW' | 'DENY';
  allowedScopes: string[];
}

interface PermissionContextType {
  permissions: Permission[];
  hasPermission: (code: string) => boolean;
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  hasPermission: () => false,
  loading: true
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { organizationId, loading: tenantLoading } = useTenant();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setPermissions([]); setLoading(true);
    const fetchPermissions = async () => {
      if (!user || !organizationId) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get<Permission[]>('/api/v1/auth/resolved', {
          params: { userId: user.uid, orgId: organizationId }
        });
        if (!cancelled) setPermissions(response.data);
      } catch (error) {
        if (!cancelled) setPermissions([]);
        console.error('Failed to fetch resolved permissions:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (!tenantLoading) {
      fetchPermissions();
    }
    return () => { cancelled = true; };
  }, [user, organizationId, tenantLoading]);

  const hasPermission = (code: string) => {
    return permissions.some(p => p.permissionCode === code && p.effect === 'ALLOW');
  };

  return (
    <PermissionContext.Provider value={{ permissions, hasPermission, loading }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);

